import { useMutateGenerateResignedTokens } from '../../services/queryClient';
import { useNavigationWithQuery } from '../authentication/useNavigationWithQuery';
import { CognitoUser } from '../../types/user';

import { useAuthenticatedUser } from './useAuthenticatedUser';
import { useClient } from './useClient';
import { useSessionStorage } from './useSessionStorage';

type GetResignedTokensCallback = (access: string, id: string) => void;
type CognitoTokens = {
  access: string;
  id: string;
  refresh?: string;
};

export function useResignedTokens() {
  const { clientId, getMfaToken } = useClient();

  const { authenticatedUser, refreshAuthenticatedUser } = useAuthenticatedUser({
    bypassCache: false,
  });
  const { getAuthItem, setAuthItem } = useSessionStorage();
  const generateNewResignedTokens = useMutateGenerateResignedTokens();

  const { navigateTo } = useNavigationWithQuery();

  const getCognitoTokens = (user?: CognitoUser): CognitoTokens | undefined => {
    if (user && user?.signInUserSession)
      return {
        access: user?.signInUserSession.accessToken.jwtToken,
        id: user?.signInUserSession.idToken.jwtToken,
        // refresh: user?.signInUserSession.refreshToken.token,
      };
    if (authenticatedUser?.signInUserSession)
      return {
        access: authenticatedUser?.signInUserSession.accessToken.jwtToken,
        id: authenticatedUser?.signInUserSession.idToken.jwtToken,
        // refresh: authenticatedUser?.signInUserSession.refreshToken.token,
      };
    return;
  };

  const getResignedTokens = () => {
    return getAuthItem('raccess')
      ? {
          access: getAuthItem('raccess'),
          id: getAuthItem('rid'),
          cognito: getAuthItem('rorigin'),
        }
      : undefined;
  };

  const updateResignedTokens = (
    resignedAccess: string,
    resignedId: string,
    cognitoAccess: string
  ) => {
    setAuthItem(`rorigin`, cognitoAccess || '');
    setAuthItem(`raccess`, resignedAccess || '');
    setAuthItem(`rid`, resignedId || '');
  };

  const relogin = () => {
    navigateTo({
      path: 'signout',
      query: {
        relogin: 1, // To ensure signout doesnt redirect to app for APS
      },
    });
  };

  const generateResignedTokens = async (
    cognitoTokens: CognitoTokens,
    callback: GetResignedTokensCallback
  ) => {
    const mfaToken = getMfaToken();
    generateNewResignedTokens.mutate(
      {
        token: cognitoTokens?.id || '',
        refreshToken: cognitoTokens?.refresh || '',
        mfaToken: mfaToken || '',
        clientId,
      },
      {
        onSuccess: ({ access, id }) => {
          if (access && id) {
            updateResignedTokens(access, id, cognitoTokens?.access || '');
            callback(access, id);
          } else {
            relogin();
          }
        },
        onError: () => {
          relogin();
        },
      }
    );
  };

  const fetchResignedTokens = async (callback: GetResignedTokensCallback) => {
    const resignedTokens = getResignedTokens();
    let cognitoTokens = getCognitoTokens();
    if (!resignedTokens) {
      if (!cognitoTokens) {
        const user = await refreshAuthenticatedUser();
        cognitoTokens = getCognitoTokens(user);
      }
      if (cognitoTokens) generateResignedTokens(cognitoTokens, callback);
      else relogin();
    } else if (
      cognitoTokens?.access &&
      cognitoTokens.access === resignedTokens.cognito
    ) {
      callback(resignedTokens.access, resignedTokens.id);
    } else if (cognitoTokens) generateResignedTokens(cognitoTokens, callback);
    else relogin();
  };

  return {
    fetchResignedTokens,
    fetchingResignedTokens: generateNewResignedTokens.isLoading,
  } as const;
}
